import { useEffect, useMemo, useState, Fragment } from 'react';
import {
    Box, Button, Grid, MenuItem, Paper, TextField, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Collapse, CircularProgress,
} from '@mui/material';
import {
    KeyboardArrowDown as ArrowDownIcon,
    KeyboardArrowRight as ArrowRightIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import DateRangeField from '../../components/ui/DateRangeField';

const STATUS_LABELS = {
    1: 'Не запланировано',
    2: 'Запланировано',
    3: 'Отгрузка разрешена',
    4: 'Идёт приёмка',
    5: 'Принято',
    6: 'Отгружено на воротах',
};

function ymd(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

function num(v) {
    if (v == null || v === '') return '-';
    const n = Number(v);
    return isNaN(n) ? '-' : n.toLocaleString('ru-RU');
}

function date(v) {
    if (!v) return '-';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('ru-RU');
}

const CS = { py: 0.5, px: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' };
const HS = { ...CS, fontWeight: 'bold', bgcolor: 'grey.100' };

function GoodsSubTable({ goods }) {
    const cs = { py: 0.3, px: 1, fontSize: '0.75rem', whiteSpace: 'nowrap' };
    const hs = { ...cs, fontWeight: 'bold' };

    if (!goods.length) return <Typography variant="body2" color="text.secondary">Нет товаров</Typography>;

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={hs}>Баркод</TableCell>
                    <TableCell sx={hs}>Артикул</TableCell>
                    <TableCell sx={hs}>NmID</TableCell>
                    <TableCell sx={hs}>Размер</TableCell>
                    <TableCell sx={hs}>Цвет</TableCell>
                    <TableCell sx={hs} align="right">В поставке</TableCell>
                    <TableCell sx={hs} align="right">Принято</TableCell>
                    <TableCell sx={hs} align="right">В продажу</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {goods.map((g, i) => (
                    <TableRow key={i}>
                        <TableCell sx={cs}>{g.barcode || '-'}</TableCell>
                        <TableCell sx={cs}>{g.vendor_code || '-'}</TableCell>
                        <TableCell sx={cs}>{g.nm_id || '-'}</TableCell>
                        <TableCell sx={cs}>{g.tech_size || '-'}</TableCell>
                        <TableCell sx={cs}>{g.color || '-'}</TableCell>
                        <TableCell sx={cs} align="right">{g.quantity ?? '-'}</TableCell>
                        <TableCell sx={cs} align="right">{g.accepted_quantity ?? '-'}</TableCell>
                        <TableCell sx={cs} align="right">{g.ready_for_sale_quantity ?? '-'}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

export default function AnalyticsSupplies() {
    const { error: showError } = useNotify();

    const today = new Date();
    const d30 = new Date(today);
    d30.setDate(today.getDate() - 30);

    const [dateFrom, setDateFrom] = useState(ymd(d30));
    const [dateTo, setDateTo] = useState(ymd(today));
    const [accounts, setAccounts] = useState([]);
    const [accountId, setAccountId] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});
    const [goodsMap, setGoodsMap] = useState({});
    const [goodsBusy, setGoodsBusy] = useState({});

    useEffect(() => {
        axios.get('/analytics/supplies/accounts')
            .then((r) => setAccounts(r.data.rows || []))
            .catch(() => {});
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (accountId) params.account_id = accountId;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const resp = await axios.get('/analytics/supplies', { params });
            setRows(resp.data.rows || []);
            setExpanded({});
            setGoodsMap({});
        } catch (e) {
            showError(e?.response?.data?.message || 'Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const toggle = async (row) => {
        const k = row.id;
        if (expanded[k]) {
            setExpanded((p) => ({ ...p, [k]: false }));
            return;
        }
        setExpanded((p) => ({ ...p, [k]: true }));
        if (!goodsMap[k]) {
            setGoodsBusy((p) => ({ ...p, [k]: true }));
            try {
                const r = await axios.get('/analytics/supplies/' + k + '/goods');
                setGoodsMap((p) => ({ ...p, [k]: r.data.rows || [] }));
            } catch {
                setGoodsMap((p) => ({ ...p, [k]: [] }));
            } finally {
                setGoodsBusy((p) => ({ ...p, [k]: false }));
            }
        }
    };

    const nameMap = useMemo(() => {
        const m = {};
        accounts.forEach((a) => { m[a.id] = a.name; });
        return m;
    }, [accounts]);

    const multiAcc = accounts.length > 1;
    const colSpan = multiAcc ? 15 : 14;

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Поставки WB</Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <TextField select label="Аккаунт WB" value={accountId}
                            onChange={(e) => setAccountId(e.target.value)} size="small" fullWidth>
                            <MenuItem value="">Все</MenuItem>
                            {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <DateRangeField valueFrom={dateFrom} valueTo={dateTo}
                            onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Button variant="contained" onClick={loadData} disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />} fullWidth>
                            Загрузить
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={HS} width={40} />
                            <TableCell sx={HS}>{'№'} поставки</TableCell>
                            {multiAcc && <TableCell sx={HS}>Аккаунт</TableCell>}
                            <TableCell sx={HS}>Статус</TableCell>
                            <TableCell sx={HS}>Плановая дата отгрузки</TableCell>
                            <TableCell sx={HS}>Факт. дата</TableCell>
                            <TableCell sx={HS}>Склад</TableCell>
                            <TableCell sx={HS} align="right">В поставке, шт</TableCell>
                            <TableCell sx={HS} align="right">Принято, шт</TableCell>
                            <TableCell sx={HS} align="right">В продажу, шт</TableCell>
                            <TableCell sx={HS} align="right">Продано, шт</TableCell>
                            <TableCell sx={HS} align="right">Возвраты, шт</TableCell>
                            <TableCell sx={HS} align="right">Итого продано, шт</TableCell>
                            <TableCell sx={HS} align="right">Остаток, шт</TableCell>
                            <TableCell sx={HS} align="right">Нераспродано, шт</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    Нет данных
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && rows.map((row) => (
                            <Fragment key={row.id}>
                                <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(row)}>
                                    <TableCell sx={CS}>
                                        <IconButton size="small">
                                            {expanded[row.id] ? <ArrowDownIcon fontSize="small" /> : <ArrowRightIcon fontSize="small" />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={CS}>{row.supply_id != null ? row.supply_id : 'заказ #' + row.preorder_id}</TableCell>
                                    {multiAcc && <TableCell sx={CS}>{nameMap[row.account_id] || row.account_id}</TableCell>}
                                    <TableCell sx={CS}>{STATUS_LABELS[row.status_id] || row.status_id}</TableCell>
                                    <TableCell sx={CS}>{date(row.supply_date)}</TableCell>
                                    <TableCell sx={CS}>{date(row.fact_date)}</TableCell>
                                    <TableCell sx={CS}>{row.actual_warehouse_name || row.warehouse_name || '-'}</TableCell>
                                    <TableCell sx={CS} align="right">{num(row.quantity)}</TableCell>
                                    <TableCell sx={CS} align="right">{num(row.accepted_quantity)}</TableCell>
                                    <TableCell sx={CS} align="right">{num(row.ready_for_sale_quantity)}</TableCell>
                                    <TableCell sx={CS} align="right">{num(row.report_sold)}</TableCell>
                                    <TableCell sx={CS} align="right">{num(row.report_returned)}</TableCell>
                                    <TableCell sx={CS} align="right">{num(row.report_net_sold)}</TableCell>
                                    <TableCell sx={CS} align="right">{num(row.report_stock_remaining)}</TableCell>
                                    <TableCell sx={CS} align="right">{num(row.report_unsold)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ p: 0 }} colSpan={colSpan}>
                                        <Collapse in={!!expanded[row.id]} timeout="auto" unmountOnExit>
                                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                {goodsBusy[row.id]
                                                    ? <CircularProgress size={20} />
                                                    : <GoodsSubTable goods={goodsMap[row.id] || []} />}
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
